(() => {
  'use strict';

  function promotionPriceState(variant, data, now = Date.now()) {
    const active = Boolean(data.promotionEligible && variant && variant.price > 0 && !variant.requiresSellingPlan && now < Number(data.promotionEndsAt));
    return {
      active,
      amount: active ? variant.salePrice : variant?.price,
      text: active ? variant.salePriceText : variant?.priceText,
      showCompare: Boolean(variant && variant.price > 0 && !active && variant.compareAtPrice > variant.price)
    };
  }

  function initializeProduct(root) {
    if (root.dataset.jmoInitialized === 'true') return;
    let data;
    try {
      data = JSON.parse(root.querySelector('[data-jmo-product-json]').textContent);
      if (!Array.isArray(data.variants)) return;
    } catch (_) { return; }
    root.dataset.jmoInitialized = 'true';
    const { variants, strings } = data;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const media = Array.from(root.querySelectorAll('[data-media-id]'));
    const thumbnails = Array.from(root.querySelectorAll('[data-media-target]'));
    const gallery = root.querySelector('[data-jmo-gallery]');
    let activeMediaId = null;
    let thumbnailFrame = 0;
    const revealSelectedThumbnail = () => {
      const activeThumbnail = thumbnails.find(button => button.dataset.mediaTarget === activeMediaId);
      if (!activeThumbnail) return;
      const rail = activeThumbnail.parentElement;
      const vertical = window.getComputedStyle(rail).flexDirection === 'column';
      const railBounds = rail.getBoundingClientRect();
      const thumbBounds = activeThumbnail.getBoundingClientRect();
      const current = vertical ? rail.scrollTop : rail.scrollLeft;
      const visible = vertical ? rail.clientHeight : rail.clientWidth;
      if (visible <= 0) return;
      const start = current + (vertical ? thumbBounds.top - railBounds.top - rail.clientTop : thumbBounds.left - railBounds.left - rail.clientLeft);
      const size = vertical ? thumbBounds.height : thumbBounds.width;
      if (start < current + 1 || start + size > current + visible - 1) {
        rail.scrollTo({ [vertical ? 'top' : 'left']: Math.max(0, start - (visible - size) / 2), behavior: 'auto' });
      }
    };
    const scheduleThumbnailReveal = () => {
      if (thumbnailFrame) window.cancelAnimationFrame(thumbnailFrame);
      thumbnailFrame = window.requestAnimationFrame(() => {
        thumbnailFrame = 0;
        revealSelectedThumbnail();
      });
    };

    const chooseMedia = (id, animate = false) => {
      const selected = media.find(item => item.dataset.mediaId === String(id));
      if (!selected) return;
      if (selected.dataset.mediaId === activeMediaId) {
        scheduleThumbnailReveal();
        return;
      }
      activeMediaId = selected.dataset.mediaId;
      const counter = root.querySelector('[data-jmo-media-counter]');
      if (counter) counter.textContent = `${media.indexOf(selected) + 1} / ${media.length}`;
      media.forEach(item => {
        const active = item === selected;
        if (!active && !item.hidden) {
          item.querySelectorAll('video').forEach(video => video.pause());
          item.querySelectorAll('iframe').forEach(frame => {
            const source = frame.getAttribute('src');
            if (source) frame.setAttribute('src', source);
          });
          item.querySelectorAll('model-viewer').forEach(model => { if (typeof model.pause === 'function') model.pause(); });
        }
        item.hidden = !active;
        item.classList.toggle('is-active', active);
      });
      thumbnails.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.mediaTarget === activeMediaId)));
      scheduleThumbnailReveal();
      if (animate && !reducedMotion.matches && typeof selected.animate === 'function') {
        selected.animate([{ opacity: 0.55 }, { opacity: 1 }], { duration: 160, easing: 'ease-out' });
      }
    };
    const stepMedia = direction => {
      const index = media.findIndex(item => item.dataset.mediaId === activeMediaId);
      chooseMedia(media[(index + direction + media.length) % media.length]?.dataset.mediaId, true);
    };
    if (media.length) {
      root.classList.add('is-enhanced');
      root.querySelector('[data-jmo-thumbnails]')?.removeAttribute('hidden');
      root.querySelector('[data-jmo-gallery-controls]')?.removeAttribute('hidden');
      chooseMedia((media.find(item => item.classList.contains('is-active')) || media[0]).dataset.mediaId);
      root.querySelectorAll('[data-jmo-media-step]').forEach(control => control.addEventListener('click', () => stepMedia(Number(control.dataset.jmoMediaStep))));
      thumbnails.forEach((button, index) => {
        button.addEventListener('click', () => chooseMedia(button.dataset.mediaTarget, true));
        button.addEventListener('keydown', event => {
          let next;
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % thumbnails.length;
          if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + thumbnails.length) % thumbnails.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = thumbnails.length - 1;
          if (next === undefined) return;
          event.preventDefault();
          thumbnails[next].focus({ preventScroll: true });
          chooseMedia(thumbnails[next].dataset.mediaTarget, true);
        });
      });
      gallery?.addEventListener('keydown', event => {
        if (event.target !== gallery) return;
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        stepMedia(event.key === 'ArrowRight' ? 1 : -1);
      });
      let gesture = null;
      gallery?.addEventListener('pointerdown', event => {
        if (event.pointerType === 'mouse' || event.target.closest('a, button, video, iframe, model-viewer')) return;
        gesture = { x: event.clientX, y: event.clientY, id: event.pointerId };
      });
      gallery?.addEventListener('pointerup', event => {
        if (!gesture || gesture.id !== event.pointerId) return;
        const dx = event.clientX - gesture.x;
        const dy = event.clientY - gesture.y;
        gesture = null;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.4) stepMedia(dx < 0 ? 1 : -1);
      });
      gallery?.addEventListener('pointercancel', () => { gesture = null; });
      const rail = root.querySelector('[data-jmo-thumbnails]');
      if (rail && typeof ResizeObserver === 'function') {
        const railObserver = new ResizeObserver(scheduleThumbnailReveal);
        railObserver.observe(rail);
        (root.closest('.shopify-section') || root).addEventListener('shopify:section:unload', () => {
          railObserver.disconnect();
          if (thumbnailFrame) window.cancelAnimationFrame(thumbnailFrame);
        }, { once: true });
      }
    }

    const initializeDialog = (dialog, openers, onOpen) => {
      if (!dialog || typeof dialog.showModal !== 'function') return;
      let opener;
      openers.forEach(link => link.addEventListener('click', event => {
        event.preventDefault();
        opener = link;
        onOpen?.(link);
        dialog.showModal();
      }));
      dialog.addEventListener('click', event => {
        const bounds = dialog.getBoundingClientRect();
        if (event.target === dialog && (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom)) dialog.close();
      });
      dialog.addEventListener('close', () => opener?.focus({ preventScroll: true }));
    };
    const zoomImage = root.querySelector('[data-jmo-zoom-image]');
    initializeDialog(root.querySelector('[data-jmo-zoom-dialog]'), root.querySelectorAll('[data-jmo-zoom]'), link => {
      zoomImage.src = link.href;
      zoomImage.alt = link.dataset.zoomAlt || '';
    });
    initializeDialog(root.querySelector('[data-jmo-size-guide-dialog]'), root.querySelectorAll('[data-jmo-size-guide-open]'));

    const inquiryLinks = Array.from(root.querySelectorAll('[data-jmo-inquiry-link]'));
    const updateInquiryLinks = (variant, options) => {
      inquiryLinks.forEach(link => {
        const contactURL = new URL(link.href, window.location.origin);
        const productURL = new URL(data.productUrl, window.location.origin);
        if (variant) {
          contactURL.searchParams.set('variant', variant.id);
          productURL.searchParams.set('variant', variant.id);
        } else contactURL.searchParams.delete('variant');
        contactURL.searchParams.set('product_url', productURL.toString());
        for (let index = 0; index < 3; index += 1) {
          const nameKey = `option_name_${index + 1}`;
          const valueKey = `option_value_${index + 1}`;
          const name = data.optionNames?.[index];
          const value = (variant?.options || options)?.[index];
          if (name && value) {
            contactURL.searchParams.set(nameKey, name);
            contactURL.searchParams.set(valueKey, value);
          } else {
            contactURL.searchParams.delete(nameKey);
            contactURL.searchParams.delete(valueKey);
          }
        }
        link.href = contactURL.toString();
      });
    };
    const form = root.querySelector('.jmo-product__form');
    if (!form || root.dataset.quoteRequired === 'true') return;
    const nativeSelect = form.querySelector('[data-jmo-variant-select]');
    const button = form.querySelector('[data-jmo-add]');
    const status = form.querySelector('[data-jmo-product-status]');
    const quantity = form.querySelector('[data-jmo-quantity]');
    const optionGroups = form.querySelector('[data-jmo-option-groups]');
    const groups = Array.from(form.querySelectorAll('[data-option-position]'));
    const radios = Array.from(form.querySelectorAll('[data-jmo-option]'));
    const price = root.querySelector('[data-jmo-price]');
    const compare = root.querySelector('[data-jmo-compare]');
    const priceGroup = root.querySelector('[data-jmo-price-group]');
    const beforePromotion = root.querySelector('[data-jmo-before-promotion]');
    const beforePrice = root.querySelector('[data-jmo-before-price]');
    const saleBadge = root.querySelector('[data-jmo-sale-badge]');
    const promotionNote = root.querySelector('.jmo-product__information > .jmo-order-discount-note');
    const structuredData = root.querySelector('[data-jmo-product-structured-data]');
    let productSchema;
    try { productSchema = structuredData ? JSON.parse(structuredData.textContent) : null; } catch (_) { productSchema = null; }
    const availability = root.querySelector('[data-jmo-availability]');
    const availabilityText = root.querySelector('[data-jmo-availability-text]');
    const availabilityLink = root.querySelector('[data-jmo-availability-link]');
    if (!nativeSelect || !button || !status || !price || !strings) return;
    let selected = variants.find(variant => variant.id === nativeSelect.value) || null;
    let submitting = false;
    const canBuy = variant => Boolean(variant && variant.available && variant.price > 0 && !variant.requiresSellingPlan);
    const updatePrice = variant => {
      const display = promotionPriceState(variant, data);
      price.textContent = variant ? variant.price > 0 ? display.text : strings.priceRequest : strings.chooseOptions;
      if (beforePromotion) beforePromotion.hidden = !display.active;
      if (beforePrice) beforePrice.textContent = variant?.priceText || '';
      if (saleBadge) saleBadge.hidden = !display.active;
      if (promotionNote) promotionNote.hidden = !display.active;
      if (priceGroup) {
        priceGroup.dataset.jmoCatalogPriceText = variant?.priceText || strings.chooseOptions;
        priceGroup.dataset.jmoCatalogPrice = String(variant?.price || 0);
        priceGroup.dataset.jmoComparePrice = String(variant?.compareAtPrice || 0);
        priceGroup.dataset.jmoCompareText = variant?.compareText || '';
      }
      if (compare) {
        compare.textContent = variant?.compareText || '';
        compare.hidden = !display.showCompare;
      }
      if (structuredData && productSchema) {
        const url = new URL(data.productAbsoluteUrl, window.location.origin);
        if (variant) url.searchParams.set('variant', variant.id);
        productSchema.url = url.toString();
        if (variant?.sku) productSchema.sku = variant.sku;
        else delete productSchema.sku;
        if (variant && variant.price > 0 && !variant.requiresSellingPlan) {
          productSchema.offers = { '@type': 'Offer', url: url.toString(), price: display.amount / 100, priceCurrency: data.currency, availability: `https://schema.org/${variant.available ? 'InStock' : 'OutOfStock'}` };
          if (display.active) productSchema.offers.priceValidUntil = '2026-10-31';
        } else delete productSchema.offers;
        structuredData.textContent = JSON.stringify(productSchema).replace(/</g, '\\u003c');
      }
    };
    const checkedOptions = () => groups.map(group => group.querySelector('input:checked')?.value);
    const findSelected = () => {
      if (!optionGroups || optionGroups.hidden) return variants.find(variant => variant.id === nativeSelect.value) || null;
      const options = checkedOptions();
      return variants.find(variant => variant.options.length === options.length && variant.options.every((option, index) => option === options[index])) || null;
    };
    const updateOptionAvailability = () => {
      const options = checkedOptions();
      groups.forEach(group => {
        const output = group.querySelector('[data-jmo-selected-option]');
        if (output) output.textContent = group.querySelector('input:checked')?.value || '';
      });
      radios.forEach(radio => {
        const position = Number(radio.dataset.jmoOption) - 1;
        const matches = variants.filter(variant => variant.options[position] === radio.value && variant.options.slice(0, position).every((option, index) => option === options[index]));
        const exists = matches.length > 0;
        const available = matches.some(canBuy);
        radio.closest('label').dataset.unavailable = String(!exists);
        const note = radio.closest('label').querySelector('[data-jmo-option-availability]');
        if (note) note.textContent = available ? '' : ` — ${exists ? strings.contact : strings.optionUnavailable}`;
      });
    };
    const updateQuantity = variant => {
      if (!quantity) return;
      const rule = variant?.quantityRule || {};
      quantity.min = String(rule.min || 1);
      quantity.step = String(rule.increment || 1);
      if (rule.max) quantity.max = String(rule.max);
      else quantity.removeAttribute('max');
      if (!quantity.checkValidity()) quantity.value = quantity.min;
      const minus = form.querySelector('[data-jmo-quantity-step="-1"]');
      const plus = form.querySelector('[data-jmo-quantity-step="1"]');
      if (minus) minus.disabled = Number(quantity.value) <= Number(quantity.min);
      if (plus) plus.disabled = quantity.max !== '' && Number(quantity.value) >= Number(quantity.max);
    };
    const update = (variant, updateURL, changeMedia = true) => {
      selected = variant;
      submitting = false;
      nativeSelect.value = variant ? variant.id : '';
      nativeSelect.disabled = !variant;
      button.disabled = !canBuy(variant);
      button.hidden = !canBuy(variant);
      button.textContent = !variant ? strings.chooseCombination : canBuy(variant) ? strings.add : strings.contact;
      status.textContent = !variant ? strings.unavailableDetail : variant.price <= 0 ? strings.priceRequestDetail : variant.requiresSellingPlan ? strings.contactDetail : variant.available ? '' : strings.availabilityDetail;
      updatePrice(variant);
      if (availability && availabilityText) {
        availability.dataset.state = canBuy(variant) ? 'available' : 'unavailable';
        availabilityText.textContent = canBuy(variant) ? strings.available : !variant ? strings.unavailable : strings.contact;
      }
      if (availabilityLink) availabilityLink.hidden = canBuy(variant);
      updateInquiryLinks(variant, checkedOptions());
      updateOptionAvailability();
      updateQuantity(variant);
      if (changeMedia && variant?.mediaId) chooseMedia(variant.mediaId, updateURL);
      if (updateURL && window.history?.replaceState) {
        const url = new URL(window.location.href);
        if (variant) url.searchParams.set('variant', variant.id);
        else url.searchParams.delete('variant');
        window.history.replaceState(window.history.state, '', url.toString());
      }
    };

    if (optionGroups && variants.length < 250) {
      if (selected) radios.forEach(radio => { radio.checked = selected.options[Number(radio.dataset.jmoOption) - 1] === radio.value; });
      optionGroups.hidden = false;
      form.querySelector('[data-jmo-native-picker]').hidden = true;
      radios.forEach(radio => radio.addEventListener('change', () => update(findSelected(), true)));
    } else if (!optionGroups) {
      form.querySelector('[data-jmo-native-picker]').hidden = true;
    }
    nativeSelect.addEventListener('change', () => {
      const variant = variants.find(item => item.id === nativeSelect.value) || null;
      if (variant) radios.forEach(radio => { radio.checked = variant.options[Number(radio.dataset.jmoOption) - 1] === radio.value; });
      update(variant, true);
    });
    form.querySelectorAll('[data-jmo-quantity-step]').forEach(control => {
      control.hidden = false;
      control.addEventListener('click', () => {
        if (!quantity) return;
        if (Number(control.dataset.jmoQuantityStep) > 0) quantity.stepUp();
        else quantity.stepDown();
        quantity.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
    quantity?.addEventListener('change', () => updateQuantity(selected));
    update(selected, false, root.dataset.jmoInitialCover !== 'true');
    document.addEventListener('jmo:promotion-expired', () => updatePrice(selected));
    window.addEventListener('pageshow', () => updatePrice(selected));
    document.addEventListener('visibilitychange', () => updatePrice(selected));
    form.addEventListener('submit', event => {
      if (!canBuy(selected) || submitting || !quantity?.checkValidity()) {
        event.preventDefault();
        if (!canBuy(selected)) update(selected, false, false);
        else if (!quantity?.checkValidity()) quantity?.reportValidity();
        return;
      }
      submitting = true;
      button.disabled = true;
      button.textContent = strings.adding;
      status.textContent = strings.openingBag;
    });
    window.addEventListener('pageshow', () => { if (submitting) update(selected, false, false); });
  }

  const initialize = (scope = document) => {
    if (scope.matches?.('[data-jmo-product]')) initializeProduct(scope);
    scope.querySelectorAll('[data-jmo-product]').forEach(initializeProduct);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
})();
